import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { formatDate } from '../utils/dateFormatter';
import { formatOrderPoNumber } from '../utils/orderPoNumber';
import {
  resolveOrderedOnDate,
  resolveStickerSizeOrder,
  resolveTotalStickersForOrder,
  sumLinePieceCount,
  buildStickerNotesText
} from '../utils/jobStickerFields';
import { LINE_TYPES } from '../firebase/schema';
import Barcode from 'react-barcode';
import { isZebraStickerPrinter, stickerElementToBase64 } from '../utils/thermalStickerPdf';

const StickerContainer = styled.div`
  width: 2.3125in;
  min-height: 4in;
  background: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 8pt;
  line-height: 1.2;
  padding: 0.2in 0.05in 0.05in 0.08in;
  box-sizing: border-box;
  color: #222 !important;
  /* border: 1px solid #222; */
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  position: relative;
  border-bottom: 1px solid #222;
  margin-bottom: 0;

  > div {
    padding: 0.03in;
    position: relative;
    
    &:not(:last-child) {
      border-right: 1px solid #222;
    }
  }
`;

const TopHeader = styled.div`
  font-size: 5.8pt;
  color: #222;
  text-transform: uppercase;
  margin-bottom: 1px;
`;

const TopValue = styled.div`
  font-size: 7.2pt;
  color: #222;
`;

const TotalStickers = styled.div`
  position: absolute;
  top: -0.08in;
  left: 0;
  font-size: 5.8pt;
  color: #222;
  text-transform: uppercase;
`;

const InvoiceNumber = styled.div`
  position: absolute;
  top: -0.08in;
  right: 0;
  font-size: 5.8pt;
  color: #222;
  text-transform: uppercase;
`;

const CustomerName = styled.div`
  font-size: 8pt;
  color: #222;
  border-bottom: 1px solid #222;
  padding: 0.03in;
  margin: 0;
`;

const DesignName = styled.div`
  text-align: center;
  font-size: 23pt;
  font-weight: 500;
  color: #222;
  border-bottom: 1px solid #222;
  padding: 0.04in;
  margin: 0;
`;

const GarmentGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  border-bottom: 1px solid #222;
  margin: 0;
`;

const GarmentLabel = styled.div`
  font-size: 7.2pt;
  text-transform: uppercase;
  color: #222;
  padding: 0.03in;
  border-right: 1px solid #222;
  border-bottom: ${props => props.borderBottom ? '1px solid #222' : 'none'};
`;

const GarmentValue = styled.div`
  padding: 0.03in;
  color: #222;
  border-bottom: ${props => props.borderBottom ? '1px solid #222' : 'none'};
`;

const InkSection = styled.div`
  border-bottom: 1px solid #222;
  margin: 0;
`;

const InkHeader = styled.div`
  display: grid;
  grid-template-columns: auto 1fr 1fr 1fr;
`;

const InkLabel = styled.div`
  font-size: 7.2pt;
  text-transform: uppercase;
  color: #222;
  padding: 0.04in;
  border-right: 1px solid #222;
`;

/** Line type label that wraps (e.g. "Screen" / "Print" on two lines) */
const InkLabelType = styled(InkLabel)`
  max-width: 0.5in;
  white-space: pre-line;
  word-break: break-word;
`;

const DtfDtgPrintRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.12in;
  font-size: 7.2pt;
  text-transform: uppercase;
  color: #222;
  padding: 0.04in;
  border-right: 1px solid #222;
  flex-wrap: wrap;

  .loc {
    display: flex;
    align-items: center;
    gap: 0.04in;
  }
  .check {
    font-size: 8pt;
  }
`;

const InkCell = styled.div`
  text-align: center;
  border-right: ${props => props.borderRight ? '1px solid #222' : 'none'};
  display: flex;
  flex-direction: column;
  
  .count {
    font-size: 7.2pt;
    padding: 0.02in;
    color: #222;
    border-bottom: 1px solid #222;
  }
  
  .colors {
    display: grid;
    grid-template-rows: repeat(4, 0.12in);
    font-size: 6.5pt;
    color: #222;
    padding: 0 0.02in;
  }
`;

const SizesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 0;
  border-bottom: 1px solid #222;
`;

const SizeHeader = styled.th`
  border-right: 1px solid #222;
  border-bottom: 1px solid #222;
  font-size: 6.5pt;
  font-weight: 500;
  color: #222;
  padding: 0.015in 0;
  text-align: center;
  min-width: 0.25in;
  
  &:last-child {
    border-right: none;
  }
`;

const SizeCell = styled.td`
  border-right: 1px solid #222;
  border-bottom: 1px solid #222;
  font-size: 7.2pt;
  color: #222;
  text-align: center;
  height: 0.2in;
  min-width: 0.25in;
  
  &:last-child {
    border-right: none;
  }
`;

const SmallText = styled.td`
  font-size: ${props => props.withBorders ? '4pt' : '5pt'};
  text-align: center;
  height: 0.15in;
  text-transform: uppercase;
  color: #222;
  padding: 0;
  vertical-align: bottom;
  transform: translateY(-5px);

  ${props => props.withBorders && `
    border-right: 1px solid #222;
    border-bottom: 1px solid #222;
    
    &:last-child {
      border-right: none;
    }
    
    tr:last-child & {
      border-bottom: none;
    }
  `}
`;

const NotesSection = styled.div`
  margin: 0;
  font-size: 5.8pt;
  display: grid;
  grid-template-columns: 1fr 0.6in;
  border-bottom: 1px solid #222;
  
  .notes-grid {
    display: grid;
    grid-template-rows: auto auto;
  }

  .notes-row {
    display: grid;
    grid-template-columns: 0.4in 1fr;
    border-bottom: 1px solid #222;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .label {
    color: #222;
    text-transform: uppercase;
    padding: 0.03in;
    border-right: 1px solid #222;
    font-size: 5pt;
  }
  
  .line {
    height: 0.2in;
  }

  .note-value {
    padding: 0.02in 0.03in;
    font-size: 5pt;
    color: #222;
    line-height: 1.15;
    height: 0.2in;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    box-sizing: border-box;
  }
`;

const Total = styled.div`
  padding: 0.03in;
  display: flex;
  justify-content: center;
  align-items: center;
  border-left: 1px solid #222;
  width: 100%;
  height: 100%;
  
  .value {
    font-weight: normal;
    font-size: 23pt;
    color: #222;
  }
`;

const JobSticker = ({ line, order, onPdfGenerated }) => {
  const stickerRef = useRef(null);
  const generatingRef = useRef(false);  // Add flag to prevent double generation
  
  const getSizeQty = (size) => {
    if (!line.sizes) return '';
    if (line.sizes[size] != null && line.sizes[size] !== '') return line.sizes[size];
    return '';
  };

  const sizeOrder = resolveStickerSizeOrder(line);
  const totalPieces = sumLinePieceCount(line, sizeOrder);
  const orderedOnDisplay = formatDate(resolveOrderedOnDate(line, order));
  const invoiceDisplay = formatOrderPoNumber(order);
  const totalStickersDisplay = resolveTotalStickersForOrder(order);
  const stickerNotesText = buildStickerNotesText(order);
  
  const getInkCount = (location) => {
    const colors = line[`${location}InkColors`] || [];
    return colors.length;
  };

  const getLineTypeDisplay = () => {
    const t = String(line.type || '').toLowerCase();
    if (t === 'screen' || t === 'screen print') return LINE_TYPES.SCREEN_PRINT;
    if (t === 'embroidery') return LINE_TYPES.EMBROIDERY;
    if (t === 'dtg') return LINE_TYPES.DTG;
    if (t === 'dtf') return LINE_TYPES.DTF;
    if (t === 'design_only' || t === 'design only') return LINE_TYPES.DESIGN_ONLY;
    if (Object.values(LINE_TYPES).includes(line.type)) return line.type;
    return LINE_TYPES.SCREEN_PRINT;
  };

  const isDtForDtg = () => {
    const display = getLineTypeDisplay();
    return display === LINE_TYPES.DTF || display === LINE_TYPES.DTG;
  };

  /** Normalized print locations (lowercase) for DTF/DTG */
  const getPrintLocations = () => {
    const locs = line.printLocations || [];
    return locs.map(l => String(l).toLowerCase());
  };

  const hasPrintOn = (loc) => {
    const locs = getPrintLocations();
    if (locs.length > 0) return locs.includes(loc);
    // Fallback: infer from ink arrays for legacy data
    const key = loc === 'front' ? 'frontInkColors' : loc === 'back' ? 'backInkColors' : 'otherInkColors';
    const arr = line[key] || [];
    return Array.isArray(arr) && arr.length > 0;
  };

  useEffect(() => {
    const generatePdfIfReady = async () => {
      // Only generate PDF if we have all required data and not already generating
      if (!line?.id || !order || !stickerRef.current || !onPdfGenerated || generatingRef.current) {
        return;
      }

      try {
        generatingRef.current = true;  // Set flag before generating
        const element = stickerRef.current;
        const zebra = isZebraStickerPrinter();
        const opt = {
          margin: 0,
          filename: `sticker_${line.id}.pdf`,
          image: { type: 'jpeg', quality: 1 },
          html2canvas: { 
            scale: 4,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: true,
            onclone: (clonedDoc) => {
              const clonedElement = clonedDoc.querySelector('[data-sticker]');
              if (clonedElement) {
                // Adjust to actual sticker size: 2 5/16 x 4 inches
                clonedElement.style.width = '2.3125in';  // 2 5/16 inches
                clonedElement.style.height = '4in';      // 4 inches
                clonedElement.style.backgroundColor = 'white';
                clonedElement.style.fontFamily = "'Montserrat', sans-serif";
                clonedElement.style.fontSize = '7.8pt';
                clonedElement.style.lineHeight = '1.1';
                // Adjust top padding to prevent cutoff, adjust right margin
                clonedElement.style.padding = '0.2in 0.05in 0.05in 0.08in';
                clonedElement.style.boxSizing = 'border-box';
                clonedElement.style.color = '#222';
                clonedElement.style.textTransform = 'uppercase';
                if (zebra) {
                  // Zebra leaves a gap at the top of the stock. Drop the header into that gap.
                  clonedElement.style.paddingTop = '0.46in';
                  clonedElement.style.color = '#000';
                  clonedDoc.querySelectorAll('[data-sticker-edge]').forEach((node) => {
                    node.style.top = '0.1in';
                    node.style.color = '#000';
                  });
                }
                clonedElement.style.transform = 'scale(1)';
                clonedElement.style.transformOrigin = 'top left';
              }
            }
          },
          jsPDF: { 
            unit: 'in',
            format: [2.3125, 4],  // 2 5/16 x 4 inches
            orientation: 'portrait',
            compress: true,
            precision: 16
          }
        };

        const base64data = await stickerElementToBase64(element, opt, { zebra });
        onPdfGenerated(base64data);
        generatingRef.current = false;
      } catch (error) {
        console.error('Error generating PDF:', error);
        generatingRef.current = false;  // Reset flag on error
        throw error;
      }
    };

    // Call the function
    generatePdfIfReady();
  }, [line, order, onPdfGenerated]); // Include all dependencies

  return (
    <div 
      ref={stickerRef}
      data-sticker
      style={{
        width: '2.3125in',  // 2 5/16 inches
        height: '4in',      // 4 inches
        backgroundColor: 'white',
        fontFamily: "'Montserrat', sans-serif",
        fontSize: '7.8pt',
        lineHeight: '1.1',
        padding: '0.2in 0.05in 0.05in 0.08in', // Adjusted top padding to prevent cutoff
        boxSizing: 'border-box',
        color: '#222',
        textTransform: 'uppercase',
        position: 'relative',
        overflow: 'hidden',
        transform: 'scale(1)',
        transformOrigin: 'top left'
      }}
    >
      <div
        data-sticker-edge
        style={{ 
        position: 'absolute', 
        top: '-0.02in', 
        left: '0', 
        fontSize: '5pt', 
        color: '#222', 
        textTransform: 'uppercase' 
      }}>
        Total Stickers: {totalStickersDisplay}
      </div>
      <div
        data-sticker-edge
        style={{ 
        position: 'absolute', 
        top: '-0.02in', 
        right: '0', 
        fontSize: '5pt', 
        color: '#222', 
        textTransform: 'uppercase' 
      }}>
        Invoice # {invoiceDisplay}
      </div>
      <div style={{ 
        display: 'flex', 
        borderBottom: '1px solid #222',
        marginBottom: '0.02in'
      }}>
        <div style={{ 
          padding: '0.02in', 
          position: 'relative', 
          borderRight: '1px solid #222',
          flex: 1
        }}>
          <div style={{ fontSize: '5pt', color: '#222', textTransform: 'uppercase' }}>Ordered On</div>
          <div style={{ fontSize: '7pt', color: '#222' }}>{orderedOnDisplay}</div>
        </div>
        <div style={{ 
          padding: '0.02in', 
          position: 'relative', 
          borderRight: '1px solid #222',
          flex: 1
        }}>
          <div style={{ fontSize: '5pt', color: '#222', textTransform: 'uppercase' }}>Due Date</div>
          <div style={{ fontSize: '7pt', color: '#222' }}>{formatDate(order.dueDate, 'MM.DD.YY')}</div>
        </div>
        <div style={{ 
          padding: '0.02in', 
          position: 'relative',
          flex: 1
        }}>
          <div style={{ fontSize: '5pt', color: '#222', textTransform: 'uppercase' }}>Manager</div>
          <div style={{ fontSize: '7pt', color: '#222' }}>{String(order.projectManager || order.manager || '').toUpperCase()}</div>
        </div>
      </div>
      <div style={{ 
        fontSize: '7pt', 
        color: '#222', 
        borderBottom: '1px solid #222', 
        padding: '0.02in', 
        margin: '0' 
      }}>
        {String(order.customerName || '').toUpperCase()}
      </div>
      <div style={{ 
        textAlign: 'center', 
        fontSize: '18pt', 
        fontWeight: '500', 
        color: '#222', 
        textTransform: 'uppercase',
        borderBottom: '1px solid #222', 
        padding: '0.03in', 
        margin: '0' 
      }}>
        {String(line.designName || '').toUpperCase()}
      </div>
      <GarmentGrid>
        <GarmentLabel borderBottom>Style</GarmentLabel>
        <GarmentValue borderBottom>{String(line.garmentId || '').toUpperCase()}</GarmentValue>
        <GarmentLabel>Color</GarmentLabel>
        <GarmentValue>{String(line.garmentColor || '').toUpperCase()}</GarmentValue>
      </GarmentGrid>

      <InkSection>
        {isDtForDtg() ? (
          <InkHeader style={{ gridTemplateColumns: 'auto 1fr' }}>
            <InkLabelType>{getLineTypeDisplay().replace(/ /g, '\n')}</InkLabelType>
            <DtfDtgPrintRow style={{ borderRight: 'none' }}>
              <span className="loc"><span className="check">{hasPrintOn('front') ? '☑' : '☐'}</span> Front</span>
              <span className="loc"><span className="check">{hasPrintOn('back') ? '☑' : '☐'}</span> Back</span>
              <span className="loc"><span className="check">{hasPrintOn('other') ? '☑' : '☐'}</span> Other</span>
            </DtfDtgPrintRow>
          </InkHeader>
        ) : (
          <InkHeader>
            <InkLabelType>{getLineTypeDisplay().replace(/ /g, '\n')}</InkLabelType>
            <InkCell borderRight>
              <div className="count">FRNT = {getInkCount('front')}</div>
              <div className="colors">
                {(line.frontInkColors || []).slice(0, 4).map((color, i) => (
                  <div key={i}>{color.toUpperCase()}</div>
                ))}
                {[...Array(Math.max(0, 4 - (line.frontInkColors?.length || 0)))].map((_, i) => (
                  <div key={`empty-${i}`}></div>
                ))}
              </div>
            </InkCell>
            <InkCell borderRight>
              <div className="count">BACK = {getInkCount('back')}</div>
              <div className="colors">
                {(line.backInkColors || []).slice(0, 4).map((color, i) => (
                  <div key={i}>{color.toUpperCase()}</div>
                ))}
                {[...Array(Math.max(0, 4 - (line.backInkColors?.length || 0)))].map((_, i) => (
                  <div key={`empty-${i}`}></div>
                ))}
              </div>
            </InkCell>
            <InkCell>
              <div className="count">OTR = {getInkCount('other')}</div>
              <div className="colors">
                {(line.otherInkColors || []).slice(0, 4).map((color, i) => (
                  <div key={i}>{color.toUpperCase()}</div>
                ))}
                {[...Array(Math.max(0, 4 - (line.otherInkColors?.length || 0)))].map((_, i) => (
                  <div key={`empty-${i}`}></div>
                ))}
              </div>
            </InkCell>
          </InkHeader>
        )}
      </InkSection>

      <SizesTable>
        <thead>
          <tr>
            {sizeOrder.map(size => (
              <SizeHeader key={size}>{String(size).toUpperCase()}</SizeHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {sizeOrder.map(size => (
              <SizeCell key={size}>{getSizeQty(size)}</SizeCell>
            ))}
          </tr>
          {/* One blank row above MISSING */}
          <tr>
            {sizeOrder.map(size => (
              <SmallText key={`missing-blank-${size}`}></SmallText>
            ))}
          </tr>
          <tr>
            {sizeOrder.map(size => (
              <SmallText key={`missing-${size}`} withBorders>MISSING</SmallText>
            ))}
          </tr>
          {/* One blank row above KILLS */}
          <tr>
            {sizeOrder.map(size => (
              <SmallText key={`kills-blank-${size}`}></SmallText>
            ))}
          </tr>
          <tr>
            {sizeOrder.map(size => (
              <SmallText key={`kills-${size}`} withBorders>KILLS</SmallText>
        ))}
          </tr>
        </tbody>
      </SizesTable>

      <NotesSection>
        <div className="notes-grid">
          <div className="notes-row">
            <div className="label">Notes</div>
            <div
              className={stickerNotesText ? 'note-value' : 'line'}
              title={stickerNotesText || undefined}
            >
              {String(stickerNotesText || '').toUpperCase()}
            </div>
          </div>
          <div className="notes-row">
            <div className="label">Job Notes</div>
            <div className="line"></div>
          </div>
        </div>
        <Total>
          <div className="value">{totalPieces}</div>
        </Total>
      </NotesSection>

      <BarcodeContainer>
        <Barcode
          value={line.id || 'NO-ID'}
          width={1}
          height={12}
          fontSize={0}
          margin={0}
          displayValue={false}
        />
      </BarcodeContainer>
    </div>
  );
};

const BarcodeContainer = styled.div`
  margin-top: 0.05in;
  display: flex;
  justify-content: center;
  width: 100%;

  /* Style overrides for the barcode */
  svg {
    width: 100% !important;
    height: auto !important;
    max-height: 0.2in;
  }
`;

export default JobSticker; 